import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { publicApi } from "../api/client";
import type { QRMenuData, MenuItem } from "@shared/types/menu";
import { ShoppingCart, Plus, Minus, Send, ChevronDown } from "lucide-react";

interface CartItem {
  dishId: string;
  name: string;
  price: number;
  quantity: number;
  variantId?: string;
  notes?: string;
}

export default function CustomerMenu() {
  const { token } = useParams();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [orderPlaced, setOrderPlaced] = useState(false);

  const { data: menu, isLoading, error } = useQuery<QRMenuData>({
    queryKey: ["qr-menu", token],
    queryFn: () => publicApi.getMenu(token!),
    enabled: !!token,
  });

  const placeMutation = useMutation({
    mutationFn: () =>
      publicApi.placeOrder(token!, {
        items: cart.map((c) => ({
          dishId: c.dishId,
          quantity: c.quantity,
          variantId: c.variantId,
          notes: c.notes,
        })),
        customerName: customerName || undefined,
        notes: orderNotes || undefined,
      }),
    onSuccess: (result) => {
      // If payment is required (delivery/takeaway), redirect to Stripe Checkout
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      setOrderPlaced(true);
      setCart([]);
    },
  });

  function addToCart(dish: MenuItem) {
    setCart((prev) => {
      const existing = prev.find((c) => c.dishId === dish.id);
      if (existing) {
        return prev.map((c) =>
          c.dishId === dish.id ? { ...c, quantity: c.quantity + 1 } : c,
        );
      }
      return [...prev, { dishId: dish.id, name: dish.name, price: dish.price, quantity: 1 }];
    });
  }

  function updateQuantity(dishId: string, delta: number) {
    setCart((prev) =>
      prev
        .map((c) =>
          c.dishId === dishId ? { ...c, quantity: c.quantity + delta } : c,
        )
        .filter((c) => c.quantity > 0),
    );
  }

  const totalItems = cart.reduce((s, c) => s + c.quantity, 0);
  const totalPrice = cart.reduce((s, c) => s + c.price * c.quantity, 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-gray-500 text-lg">Caricamento menu...</div>
      </div>
    );
  }

  if (error || !menu) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center px-6">
          <p className="text-red-500 text-lg font-semibold">QR code non valido o scaduto</p>
          <p className="text-gray-400 mt-2">Chiedi al cameriere un nuovo codice</p>
        </div>
      </div>
    );
  }

  if (orderPlaced) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center px-6">
          <div className="text-6xl mb-4">&#x2705;</div>
          <h2 className="text-2xl font-bold text-gray-800">Ordine inviato!</h2>
          <p className="text-gray-500 mt-2">
            Il tuo ordine è stato ricevuto. Il cameriere lo confermerà a breve.
          </p>
          <button
            onClick={() => setOrderPlaced(false)}
            className="mt-6 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium min-h-touch"
          >
            Ordina ancora
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-white shadow-sm z-10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-800">Menu</h1>
            <p className="text-sm text-gray-400">Tavolo {menu.tableName}</p>
          </div>
          <button
            onClick={() => setShowCart(!showCart)}
            className="relative bg-blue-600 text-white p-3 rounded-full min-h-touch min-w-touch flex items-center justify-center"
          >
            <ShoppingCart size={22} />
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Categories & Dishes */}
      <div className="px-4 py-4 space-y-6">
        {menu.categories.map((cat) => (
          <div key={cat.id}>
            <h2 className="text-lg font-bold text-gray-800 mb-3 sticky top-16 bg-gray-50 py-2">
              {cat.name}
            </h2>
            <div className="space-y-2">
              {cat.dishes
                .filter((d) => d.isAvailable)
                .map((dish) => {
                  const inCart = cart.find((c) => c.dishId === dish.id);
                  return (
                    <div
                      key={dish.id}
                      className="bg-white rounded-lg p-4 flex items-center justify-between shadow-sm"
                    >
                      <div className="flex-1 mr-3">
                        <p className="font-semibold text-gray-800">{dish.name}</p>
                        {dish.description && (
                          <p className="text-sm text-gray-400 mt-0.5 line-clamp-2">
                            {dish.description}
                          </p>
                        )}
                        <p className="text-blue-600 font-medium mt-1">
                          &euro;{(dish.price / 100).toFixed(2)}
                        </p>
                      </div>
                      {inCart ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(dish.id, -1)}
                            className="bg-gray-100 rounded-full p-2 min-h-touch min-w-touch flex items-center justify-center"
                          >
                            <Minus size={18} />
                          </button>
                          <span className="font-bold text-lg w-8 text-center">
                            {inCart.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(dish.id, 1)}
                            className="bg-blue-600 text-white rounded-full p-2 min-h-touch min-w-touch flex items-center justify-center"
                          >
                            <Plus size={18} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => addToCart(dish)}
                          className="bg-blue-600 text-white rounded-full p-2 min-h-touch min-w-touch flex items-center justify-center"
                        >
                          <Plus size={22} />
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        ))}
      </div>

      {/* Cart Bottom Sheet */}
      {showCart && totalItems > 0 && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div className="bg-black/40 flex-1" onClick={() => setShowCart(false)} />
          <div className="bg-white rounded-t-2xl p-6 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Il tuo ordine</h3>
              <button onClick={() => setShowCart(false)}>
                <ChevronDown size={24} className="text-gray-400" />
              </button>
            </div>

            {cart.map((item) => (
              <div key={item.dishId} className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <p className="font-medium text-gray-800">{item.name}</p>
                  <p className="text-sm text-gray-400">
                    &euro;{(item.price / 100).toFixed(2)} &times; {item.quantity}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateQuantity(item.dishId, -1)}
                    className="bg-gray-100 rounded-full p-1.5"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="font-bold w-6 text-center">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.dishId, 1)}
                    className="bg-blue-600 text-white rounded-full p-1.5"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            ))}

            <input
              type="text"
              placeholder="Il tuo nome (opzionale)"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-4 min-h-touch"
            />

            <textarea
              placeholder="Note per la cucina (opzionale)"
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-2 min-h-[80px] resize-none"
            />

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <div>
                <p className="text-sm text-gray-400">Totale</p>
                <p className="text-2xl font-bold text-gray-800">
                  &euro;{(totalPrice / 100).toFixed(2)}
                </p>
              </div>
              <button
                onClick={() => placeMutation.mutate()}
                disabled={placeMutation.isPending}
                className="bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg min-h-touch flex items-center gap-2 disabled:opacity-50"
              >
                <Send size={20} />
                {placeMutation.isPending ? "Invio..." : "Invia ordine"}
              </button>
            </div>

            {placeMutation.isError && (
              <p className="text-red-500 text-sm mt-2 text-center">
                Errore nell&apos;invio. Riprova.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Floating cart bar */}
      {totalItems > 0 && !showCart && (
        <div className="fixed bottom-0 left-0 right-0 bg-blue-600 text-white p-4 flex items-center justify-between z-40">
          <div>
            <span className="font-bold">{totalItems} {totalItems === 1 ? "piatto" : "piatti"}</span>
            <span className="ml-2 opacity-80">&euro;{(totalPrice / 100).toFixed(2)}</span>
          </div>
          <button
            onClick={() => setShowCart(true)}
            className="bg-white text-blue-600 px-6 py-2 rounded-lg font-bold min-h-touch"
          >
            Vedi ordine
          </button>
        </div>
      )}
    </div>
  );
}
